// Low-latency knobs for a 1:1 call. A photobooth needs "live mirror" feel,
// so we trade buffering for latency wherever the browser allows it.
//
// Codec (SA B8): hardware H.264 first, VP8 otherwise. We cannot know whether
// H.264 is hardware-encoded before frames flow, so:
//   1. if getCapabilities lists H.264 and this device has not been seen encoding
//      H.264 in software, H.264 is put first in the codec preferences;
//   2. after connect, `encoderImplementation` / `powerEfficientEncoder` tell the
//      truth; a software H.264 encoder flips the remembered choice to VP8, which
//      applies to the next negotiation (next session / renegotiation).
// Preferences are set on the transceiver right after the track is added, i.e.
// before the offer is created (installCodecPreferences patches addTrack /
// addTransceiver once, so room.ts does not need to know about it).

import { MAX_BITRATE, STREAM_FPS } from '../config';

const SW_H264_KEY = 'booth.codec.h264sw';

const readFlag = () => { try { return localStorage.getItem(SW_H264_KEY) === '1'; } catch { return false; } };
const writeFlag = (v: boolean) => { try { localStorage.setItem(SW_H264_KEY, v ? '1' : '0'); } catch { /* private mode */ } };

export type PreferredCodec = 'H264' | 'VP8';

/** Codec order we want for video senders right now. */
export function preferredVideoCodec(): PreferredCodec {
  const caps = typeof RTCRtpSender !== 'undefined' ? RTCRtpSender.getCapabilities?.('video') : null;
  const hasH264 = !!caps?.codecs.some((c) => /h264/i.test(c.mimeType));
  return hasH264 && !readFlag() ? 'H264' : 'VP8';
}

/** Sort capabilities so `first` comes first (keeps RTX/RED/FEC entries). */
export function orderCodecs<T extends { mimeType: string; sdpFmtpLine?: string }>(codecs: T[], first: PreferredCodec): T[] {
  const rank = (c: T) => {
    const m = c.mimeType.toLowerCase();
    if (m === `video/${first.toLowerCase()}`) {
      // prefer constrained-baseline/packetization-mode=1 for H.264 (widest hardware support)
      if (first === 'H264') return /packetization-mode=1/.test(c.sdpFmtpLine ?? '') ? 0 : 1;
      return 0;
    }
    if (m === 'video/vp8') return 2;
    if (m === 'video/h264') return 3;
    if (/rtx|red|ulpfec|flexfec/.test(m)) return 9;
    return 5;
  };
  return codecs.map((c, i) => ({ c, i })).sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i).map((x) => x.c);
}

function applyPrefs(t: RTCRtpTransceiver) {
  try {
    if (t.sender.track?.kind !== 'video' && t.receiver.track?.kind !== 'video') return;
    const caps = RTCRtpReceiver.getCapabilities?.('video') ?? RTCRtpSender.getCapabilities?.('video');
    if (!caps || typeof t.setCodecPreferences !== 'function') return;
    t.setCodecPreferences(orderCodecs(caps.codecs, preferredVideoCodec()));
  } catch (e) {
    console.warn('setCodecPreferences failed', e);
  }
}

let installed = false;
/** Idempotent: every new video transceiver gets our codec order before the offer is made. */
export function installCodecPreferences() {
  if (installed || typeof RTCPeerConnection === 'undefined') return;
  installed = true;
  const proto = RTCPeerConnection.prototype;
  const addTrack = proto.addTrack;
  proto.addTrack = function (this: RTCPeerConnection, track: MediaStreamTrack, ...streams: MediaStream[]) {
    const sender = addTrack.call(this, track, ...streams);
    if (track.kind === 'video') {
      const t = this.getTransceivers().find((x) => x.sender === sender);
      if (t) applyPrefs(t);
    }
    return sender;
  } as typeof proto.addTrack;
  const addTransceiver = proto.addTransceiver;
  proto.addTransceiver = function (this: RTCPeerConnection, trackOrKind: MediaStreamTrack | string, init?: RTCRtpTransceiverInit) {
    const t = addTransceiver.call(this, trackOrKind as any, init);
    if ((typeof trackOrKind === 'string' ? trackOrKind : trackOrKind.kind) === 'video') applyPrefs(t);
    return t;
  } as typeof proto.addTransceiver;
}

/** Returns false if the connection is not ready yet (call again later). */
export async function tuneForLatency(pc: RTCPeerConnection): Promise<boolean> {
  let ok = true;
  for (const t of pc.getTransceivers()) applyPrefs(t); // for the next renegotiation
  for (const sender of pc.getSenders()) {
    if (sender.track?.kind !== 'video') continue;
    // camera motion, not a slide deck: keep frame rate, let detail go first
    try { if ('contentHint' in sender.track) sender.track.contentHint = 'motion'; } catch { /* unsupported */ }
    const p = sender.getParameters();
    if (!p.encodings?.length) {
      ok = false; // negotiation not finished
      continue;
    }
    p.encodings[0].maxBitrate = MAX_BITRATE;
    p.encodings[0].maxFramerate = STREAM_FPS;
    try {
      await sender.setParameters(p);
    } catch (e) {
      console.warn('setParameters failed', e);
    }
    // separate call: some browsers reject degradationPreference changes.
    // maintain-resolution keeps the 16px block alignment of the packed frame (SA B8).
    try {
      const q = sender.getParameters();
      await sender.setParameters({ ...q, degradationPreference: 'maintain-resolution' } as RTCRtpSendParameters);
    } catch { /* unsupported */ }
  }
  for (const r of pc.getReceivers()) {
    // Chrome/Android WebView: ask the jitter buffer to stay as small as possible
    try {
      (r as any).jitterBufferTarget = 0;
      (r as any).playoutDelayHint = 0;
    } catch { /* unsupported */ }
  }
  return ok;
}

export interface LinkStats {
  rttMs: number;
  relayed: boolean;
  localType: string;
  remoteType: string;
  inFps: number;
  inKbps: number;
  jitterBufferMs: number;
  /** outgoing video codec, e.g. "H264" / "VP8" ('' until known) */
  codec?: string;
  /** e.g. "MediaCodec", "OpenH264", "libvpx", "ExternalEncoder" */
  encoderImpl?: string;
  /** true = hardware encoder (browser's own judgement) */
  powerEfficient?: boolean;
}

const prevBytes = new WeakMap<RTCPeerConnection, { bytes: number; t: number }>();

/** Software H.264 (OpenH264 etc.) burns battery on phones: remember to use VP8 next time. */
export function noteEncoder(codec: string | undefined, impl: string | undefined, powerEfficient: boolean | undefined) {
  if (!codec || !/h264/i.test(codec)) return;
  const software = powerEfficient === false || /openh264|software/i.test(impl ?? '');
  if (software !== readFlag()) writeFlag(software);
}

export async function readStats(pc: RTCPeerConnection): Promise<LinkStats | null> {
  const stats = await pc.getStats();
  let pair: any = null;
  const byId = new Map<string, any>();
  stats.forEach((s: any) => byId.set(s.id, s));
  stats.forEach((s: any) => {
    if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded') pair = s;
  });
  if (!pair) return null;
  const local = byId.get(pair.localCandidateId);
  const remote = byId.get(pair.remoteCandidateId);
  let inFps = 0, bytes = 0, jb = 0;
  let codec = '', encoderImpl: string | undefined, powerEfficient: boolean | undefined;
  stats.forEach((s: any) => {
    if (s.type === 'inbound-rtp' && s.kind === 'video') {
      inFps = s.framesPerSecond ?? 0;
      bytes = s.bytesReceived ?? 0;
      if (s.jitterBufferEmittedCount) jb = (s.jitterBufferDelay / s.jitterBufferEmittedCount) * 1000;
    }
    if (s.type === 'outbound-rtp' && s.kind === 'video') {
      codec = String(byId.get(s.codecId)?.mimeType ?? '').replace(/^video\//i, '');
      encoderImpl = s.encoderImplementation;
      powerEfficient = s.powerEfficientEncoder;
    }
  });
  noteEncoder(codec, encoderImpl, powerEfficient);
  const now = performance.now();
  const prev = prevBytes.get(pc);
  prevBytes.set(pc, { bytes, t: now });
  const inKbps = prev ? ((bytes - prev.bytes) * 8) / (now - prev.t) : 0;
  return {
    rttMs: (pair.currentRoundTripTime ?? 0) * 1000,
    relayed: local?.candidateType === 'relay' || remote?.candidateType === 'relay',
    localType: local?.candidateType ?? '?',
    remoteType: remote?.candidateType ?? '?',
    inFps,
    inKbps,
    jitterBufferMs: jb,
    codec,
    encoderImpl,
    powerEfficient,
  };
}
